import LayerManager from '@doordash/component-layer-manager'
import { FeatureFlagsProvider, initialize } from '@doordash/lib-feature-flags'
import * as moment from 'moment-timezone'
import * as queryString from 'query-string'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { Route, Switch } from 'react-router'
import { BrowserRouter } from 'react-router-dom'
import { ConnectedRouter } from 'react-router-redux'
import Onboarding from './components/pages/onboarding'
import Application from './components/pages/application'
import i18n from './i18n'
import createStore from './initializers/redux'
import routes from './routes'
import configureApi from './utilities/configureApi'
import PrivacyPortal from './components/pages/privacy-portal'
import WebViewWrapper from './components/organisms/webview-wrapper'
import DrivingOpportunities from './components/pages/drivingopportunities'
import WebView from './components/pages/webview'
import defaultFeatureFlags from './defaultFeatureFlags'
import experimentToFeatureFlagMap from './experimentToFeatureFlagMap'
import {
  DeliveryDispute,
  DeliveryIssueDetails,
  DeliveryIssuesList,
  Explanation,
  Takeover,
} from './components/organisms/delivery-issues/'
import FastPay from './components/molecules/fast-pay'
import { StepWrapper } from './components/organisms/dropshipping-status/step-wrapper'
import {
  SplashScreen,
  ActivatedNoFirstDashContainer,
  activatedNoFirstDashSlideDeck,
} from './components/pages/activated-no-first-dash'
import { FirstDash } from './components/pages/first-dash'
import { get } from 'lodash'
import { formatExperiments } from './utilities/formatExperiments'
import ErrorBoundary from './components/molecules/error-boundary'
import { DeliveryIssueTypes } from './models/types'
import SignUpPage from './components/pages/sign-up'
import { WrappedGetStartedPromotions } from './components/pages/get-started-promotions'
import { WrappedBankAccountSetup } from './components/pages/bank-account-setup'
import { WrappedDownloadApp } from './components/pages/download-app'
import { WrappedApplicationComplete } from './components/pages/application-complete'

type MainOptions = {
  environment: string
  initialState: any
  apiUrl?: string
}

const main = (container: HTMLElement, options: MainOptions): void => {
  const { initialState } = options
  const { getFeatureFlag, mappedExperiments } = initialize(
    experimentToFeatureFlagMap,
    defaultFeatureFlags,
    formatExperiments(initialState.experiments),
    initialState.feature_flag_overrides
  )

  const queryParams = queryString.parse(window.location.search)
  const w9Signed = queryParams.event === 'signing_complete'
  const isExtremelyLate = queryParams.mode === 'extreme_lateness'

  const { history, store } = createStore({
    ...initialState,
    ...{ experiments: mappedExperiments },
  })
  const API_URL = options.apiUrl || 'https://api.doordash.com'
  configureApi(API_URL)
  moment.locale(i18n.getCurrentLocale())

  ReactDOM.render(
    <Provider store={store}>
      <FeatureFlagsProvider getFeatureFlag={getFeatureFlag}>
        <LayerManager>
          <BrowserRouter>
            <ConnectedRouter history={history}>
              <Switch location={store.getState().router.location}>
                <Route
                  exact
                  path={routes.application.path}
                  component={props => <Application {...props} />}
                />
                <Route
                  exact
                  path={routes.dasherflow.path}
                  component={({ match }) => (
                    <Onboarding
                      uniqueLink={match.params.uniqueLink}
                      step={match.params.step}
                      w9Signed={w9Signed}
                    />
                  )}
                />
                <Route
                  exact
                  path={routes.drivingOpportunities.path}
                  component={({ match }) => (
                    <DrivingOpportunities
                      slug={match.params.slug}
                      jobType={match.params.jobType}
                      cityState={match.params.slug}
                      data={get(options, 'initialState.DATA', {})}
                      settings={get(options, 'initialState.SETTINGS', {})}
                    />
                  )}
                />
                <Route
                  exact
                  path={routes.deliveryIssueList.path}
                  render={() => {
                    return (
                      <WebViewWrapper>
                        <DeliveryIssuesList />
                      </WebViewWrapper>
                    )
                  }}
                />
                <Route
                  exact
                  path={routes.deliveryDispute.path}
                  render={props => {
                    const { match } = props
                    return (
                      <WebViewWrapper>
                        <DeliveryDispute issueId={match.params.id} {...props} />
                      </WebViewWrapper>
                    )
                  }}
                />
                <Route
                  path={routes.deliverySplash.path}
                  render={() => {
                    return (
                      <WebViewWrapper>
                        <Takeover
                          type={
                            isExtremelyLate
                              ? DeliveryIssueTypes.ExtremelyLate
                              : DeliveryIssueTypes.NeverDelivered
                          }
                        />
                      </WebViewWrapper>
                    )
                  }}
                />
                <Route
                  path={routes.deliveryIssuesExplanation.path}
                  render={props => {
                    return (
                      <WebViewWrapper>
                        <Explanation {...props} />
                      </WebViewWrapper>
                    )
                  }}
                />
                <Route
                  exact
                  path={routes.deliveryIssueDetail.path}
                  render={props => {
                    const { match } = props
                    return (
                      <WebViewWrapper>
                        <LayerManager>
                          <DeliveryIssueDetails
                            issueId={match.params.id}
                            {...props}
                          />
                        </LayerManager>
                      </WebViewWrapper>
                    )
                  }}
                />
                <Route
                  exact
                  path={routes.fastPaySetup.path}
                  component={({ match }) => {
                    return (
                      <StepWrapper>
                        <FastPay uniqueLink={match.params.uniqueLink} />
                      </StepWrapper>
                    )
                  }}
                />
                <Route
                  exact
                  path={routes.firstDash.path}
                  component={props => <SplashScreen {...props} />}
                />
                <Route
                  exact
                  path={routes.firstDashActivatedNoFirstDash.path}
                  component={props => (
                    <ActivatedNoFirstDashContainer
                      {...props}
                      slides={activatedNoFirstDashSlideDeck}
                    />
                  )}
                />
                <Route
                  exact
                  path={routes.firstDashDash.path}
                  component={props => <FirstDash {...props} />}
                />
                <Route
                  exact
                  path={routes.webview.path}
                  render={({ match }) => (
                    <ErrorBoundary>
                      <WebView sub={match.params.sub} />
                    </ErrorBoundary>
                  )}
                />
                <Route
                  exact
                  path={routes.privacy.path}
                  render={() => <PrivacyPortal />}
                />
                <Route
                  exact
                  path={routes.promotionsGetStarted.path}
                  component={props => (
                    <WrappedGetStartedPromotions {...props} />
                  )}
                />
                <Route
                  exact
                  path={routes.promotionsBankAccount.path}
                  component={props => <WrappedBankAccountSetup {...props} />}
                />
                <Route
                  exact
                  path={routes.promotionsDownloadApp.path}
                  component={props => <WrappedDownloadApp {...props} />}
                />
                <Route
                  exact
                  path={routes.promotionsApplicationComplete.path}
                  component={props => <WrappedApplicationComplete {...props} />}
                />
                <Route component={props => <SignUpPage {...props} />} />
              </Switch>
            </ConnectedRouter>
          </BrowserRouter>
        </LayerManager>
      </FeatureFlagsProvider>
    </Provider>,
    container
  )
}

export default main
